import { isDevelopment, runtimeConfig } from "./runtimeConfig";
import {
  clearSession,
  getSession,
  wasSessionRestoredWithin,
} from "./auth/sessionStore";

/** F-6 lap guard: a 401 this soon after a bootstrap restore is not an expiry. */
const SESSION_RESTORE_LAP_GUARD_MS = 10_000;

export class ApiError extends Error {
  status: number;
  data: unknown;

  constructor(message: string, status: number, data: unknown) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.data = data;
  }
}

async function parseJsonSafely(text: string): Promise<unknown> {
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

function messageFromPayload(payload: unknown, fallback: string): string {
  if (payload && typeof payload === "object") {
    const maybeMessage = (payload as { message?: unknown }).message;
    if (typeof maybeMessage === "string" && maybeMessage.trim()) {
      return maybeMessage;
    }
    const maybeError = (payload as { error?: unknown }).error;
    if (typeof maybeError === "string" && maybeError.trim()) {
      return maybeError;
    }
  }
  return fallback;
}

/**
 * Read the response body using a streaming reader with an idle timeout.
 * Works around the Quarkus Lambda mock event server sending an empty
 * `Transfer-Encoding` header with no `Content-Length`, which causes
 * `response.text()` to hang because the browser never sees end-of-body.
 */
async function readBodyText(response: Response, idleMs = 2000): Promise<string> {
  const body = response.body;
  if (!body) return "";

  const reader = body.getReader();
  const decoder = new TextDecoder();
  let result = "";

  try {
    for (;;) {
      const { done, value } = await Promise.race([
        reader.read(),
        new Promise<ReadableStreamReadResult<Uint8Array>>((resolve) =>
          setTimeout(() => resolve({ done: true, value: undefined }), idleMs),
        ),
      ]);
      if (done) break;
      if (value) result += decoder.decode(value, { stream: true });
    }
  } finally {
    reader.cancel();
  }

  return result + decoder.decode();
}

// Retry on 5xx for GET only — POSTs are not retried because data-input
// and contact submissions aren't trivially idempotent. CF Workers can
// occasionally surface backend hangs as 1101 HTML 500s; the retries
// give those a chance to recover (typical Mongo dead-socket clears in
// well under 1s on the next attempt). Total deadline ~1.3s.
const RETRYABLE_BACKOFFS_MS = [100, 300, 900] as const;

function isRetryableMethod(init: RequestInit): boolean {
  const method = (init.method ?? "GET").toUpperCase();
  return method === "GET";
}

function isHtmlContentType(response: Response): boolean {
  const ct = response.headers.get("content-type") ?? "";
  return ct.toLowerCase().startsWith("text/html");
}

async function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export async function apiFetch<T>(
  path: string,
  init: RequestInit = {},
): Promise<T> {
  const url = `${runtimeConfig.apiBase}${path}`;
  const fetchInit: RequestInit = {
    ...init,
    credentials: "include",
    cache: "no-store",
    headers: {
      "Content-Type": "application/json",
      ...(init.headers ?? {}),
    },
  };

  const retryable = isRetryableMethod(init);
  const maxAttempts = retryable ? 1 + RETRYABLE_BACKOFFS_MS.length : 1;

  let lastResponse: Response | null = null;
  let lastText = "";
  let lastPayload: unknown = null;
  let lastNetworkError: unknown = null;

  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    if (attempt > 0) {
      await sleep(RETRYABLE_BACKOFFS_MS[attempt - 1]!);
    }

    let response: Response;
    try {
      response = await fetch(url, fetchInit);
    } catch (e) {
      // Network-level error — typically `TypeError: Failed to fetch`
      // when the Worker process dies entirely (CF 1101 sometimes
      // surfaces as a connection error rather than a 500 response).
      // Retry up to maxAttempts; non-retryable methods rethrow now.
      lastNetworkError = e;
      if (!retryable) throw e;
      continue;
    }
    const text = isDevelopment ? await readBodyText(response) : await response.text();
    const payload = await parseJsonSafely(text);

    lastResponse = response;
    lastText = text;
    lastPayload = payload;
    lastNetworkError = null;

    if (response.ok) {
      return payload as T;
    }

    // Don't retry 4xx — they're not transient.
    if (!retryable || response.status < 500) break;
  }

  // Exhausted all attempts. If we never got a Response (network error
  // every time), surface that as a synthetic 0-status ApiError so the
  // caller can still pattern-match on `instanceof ApiError`.
  if (lastResponse === null) {
    const message =
      lastNetworkError instanceof Error
        ? lastNetworkError.message
        : "Network request failed";
    throw new ApiError(message, 0, lastNetworkError ?? null);
  }

  // For CF 1101 HTML pages, replace the body-derived message with a
  // clean "Service temporarily unavailable" so the SPA can render a
  // reasonable error instead of HTML noise.
  const status = lastResponse.status;
  const fallbackMessage =
    status >= 500 && isHtmlContentType(lastResponse)
      ? "Service temporarily unavailable"
      : `Request failed with status ${status}`;

  // Authenticated endpoint says session is gone. Tear down local state
  // synchronously so the next render doesn't show prior user data.
  // Skip `/auth/login` itself — a failed-credentials 401 against an
  // anonymous user shouldn't kick off a wasted bootstrap re-fetch.
  // Audit 2026-09-23 (4a): only when there IS a session — an anonymous
  // 401 (stale appointment link) has nothing to tear down, and the
  // teardown's `wb:auth-changed` is what remounted the route into a loop.
  // Re-audit 2026-09-23 (F-6): and not inside the lap window after a
  // bootstrap restore. The Worker's bootstrap keeps a VALID session cached
  // while a degraded slotting lane answers 401; tearing down here would
  // remount, restore, 401, tear down — the same loop with a logged-in guest
  // (typed values wiped, "session expired" flashed for a session that is
  // fine). The ApiError still surfaces so the widget shows its own error.
  if (
    status === 401 &&
    !path.startsWith("/auth/login") &&
    getSession() !== null &&
    !wasSessionRestoredWithin(SESSION_RESTORE_LAP_GUARD_MS)
  ) {
    try {
      clearSession("expired");
    } catch {
      /* noop */
    }
  }

  throw new ApiError(
    messageFromPayload(lastPayload, fallbackMessage),
    status,
    lastPayload ?? lastText,
  );
}

export function toSearchParams(params: Record<string, string | undefined>): string {
  const sp = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined) sp.set(key, value);
  }
  const query = sp.toString();
  return query ? `?${query}` : "";
}
